import React, { useState, useEffect, useCallback } from "react";
import {
  FaShoppingBag,
  FaSearch,
  FaEye,
  FaSyncAlt,
  FaExclamationCircle
} from "react-icons/fa";
import { fetchWithAuth } from "../../../utils/api";
import "./TopmateBookings.css";

function TopmatePurchases() {
  const [purchases, setPurchases] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [dateRange, setDateRange] = useState("all");
  const [page, setPage] = useState(1);

  const [activeDetailsPurchase, setActiveDetailsPurchase] = useState(null);

  const fetchPurchases = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const params = new URLSearchParams({
        page: String(page),
        limit: "10",
        search: search.trim(),
        status,
        dateRange
      });

      const res = await fetchWithAuth(`/admin/topmate/purchases?${params.toString()}`);
      const data = await res.json();

      if (data.success) {
        setPurchases(data.purchases || []);
        setPagination(data.pagination || { total: 0, page: 1, limit: 10, totalPages: 1 });
      } else {
        setError(data.message || "Failed to load purchases");
      }
    } catch (err) {
      setError("Network error fetching digital purchases");
    } finally {
      setLoading(false);
    }
  }, [page, search, status, dateRange]);

  useEffect(() => {
    fetchPurchases();
  }, [fetchPurchases]);

  return (
    <div className="topmate-dashboard-container">
      {/* HEADER */}
      <div className="topmate-header-card">
        <div className="topmate-header-text">
          <div className="topmate-header-badge">
            <FaShoppingBag /> Digital Product Store
          </div>
          <h1>Digital Purchases</h1>
          <p>
            Track real customer purchases, verify order amounts, review delivery status, and monitor automatic order confirmations.
          </p>
        </div>

        <div className="topmate-header-actions">
          <button className="topmate-btn-secondary" onClick={fetchPurchases} disabled={loading}>
            <FaSyncAlt className={loading ? "spin-icon" : ""} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="topmate-alert danger">
          <FaExclamationCircle /> <span>{error}</span>
        </div>
      )}

      {/* FILTERS */}
      <div className="filter-controls-bar">
        <div className="search-input-wrap">
          <FaSearch className="search-icon" />
          <input
            type="text"
            className="topmate-input"
            placeholder="Search by customer, email, order ID, product name..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <select
          className="topmate-select"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        >
          <option value="all">All Payment Statuses</option>
          <option value="Paid">Paid</option>
          <option value="Refunded">Refunded</option>
          <option value="Failed">Failed</option>
        </select>

        <select
          className="topmate-select"
          value={dateRange}
          onChange={(e) => {
            setDateRange(e.target.value);
            setPage(1);
          }}
        >
          <option value="all">All Time (IST)</option>
          <option value="today">Today (IST)</option>
          <option value="7d">Last 7 Days (IST)</option>
          <option value="30d">Last 30 Days (IST)</option>
          <option value="90d">Last 90 Days (IST)</option>
          <option value="year">This Year (IST)</option>
        </select>
      </div>

      {/* PURCHASES TABLE */}
      <div className="topmate-card">
        {loading ? (
          <div className="topmate-state-box">
            <div className="topmate-spinner" />
            <p>Loading digital purchases...</p>
          </div>
        ) : purchases.length === 0 ? (
          <div className="topmate-state-box">
            <p>No digital product purchases found.</p>
            <span style={{ fontSize: "0.8rem", color: "#64748b" }}>
              Digital purchases sent from Topmate webhooks will automatically appear here.
            </span>
          </div>
        ) : (
          <>
            <div className="topmate-table-wrapper">
              <table className="topmate-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Customer Name</th>
                    <th>Customer Email</th>
                    <th>Product Name</th>
                    <th>Order ID</th>
                    <th>Date & Time (IST)</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Email Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {purchases.map((purchase, index) => {
                    const serialNumber = (pagination.page - 1) * pagination.limit + index + 1;
                    return (
                      <tr key={purchase._id}>
                        <td>{serialNumber}</td>
                        <td>
                          <strong>{purchase.customerName}</strong>
                        </td>
                        <td style={{ color: "#94a3b8" }}>{purchase.customerEmail}</td>
                        <td>{purchase.productName}</td>
                        <td>
                          <code style={{ fontSize: "0.75rem", background: "rgba(255,255,255,0.06)", padding: "2px 6px", borderRadius: "4px" }}>
                            {purchase.topmateOrderId}
                          </code>
                        </td>
                        <td style={{ color: "#38bdf8" }}>{purchase.purchaseDateIST}</td>
                        <td style={{ color: "#10b981", fontWeight: 600 }}>
                          {purchase.currency} {purchase.amount}
                        </td>
                        <td>
                          <span className="status-pill paid">{purchase.paymentStatus}</span>
                        </td>
                        <td>
                          {purchase.emailSent ? (
                            <span className="status-pill sent">Email Sent</span>
                          ) : (
                            <span className="status-pill pending">Pending</span>
                          )}
                        </td>
                        <td>
                          <div className="table-actions-cell">
                            <button
                              className="btn-action"
                              onClick={() => setActiveDetailsPurchase(purchase)}
                              title="View purchase details"
                            >
                              <FaEye /> View
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="topmate-pagination">
              <div>
                Showing {(pagination.page - 1) * pagination.limit + 1} -{" "}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} records
              </div>
              <div className="pagination-buttons">
                <button
                  className="btn-page"
                  disabled={pagination.page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <span style={{ fontSize: "0.8rem", color: "#f8fafc" }}>
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  className="btn-page"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* VIEW DETAILS MODAL */}
      {activeDetailsPurchase && (
        <div className="topmate-modal-overlay" onClick={() => setActiveDetailsPurchase(null)}>
          <div className="topmate-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <FaShoppingBag style={{ color: "#c084fc" }} /> Purchase Details
              </h3>
              <button className="modal-close-btn" onClick={() => setActiveDetailsPurchase(null)} aria-label="Close modal">
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-details-grid">
                <div className="modal-detail-item">
                  <span className="modal-detail-label">Customer Name</span>
                  <span className="modal-detail-value">{activeDetailsPurchase.customerName}</span>
                </div>
                <div className="modal-detail-item">
                  <span className="modal-detail-label">Customer Email</span>
                  <span className="modal-detail-value">{activeDetailsPurchase.customerEmail}</span>
                </div>
                <div className="modal-detail-item">
                  <span className="modal-detail-label">Product Name</span>
                  <span className="modal-detail-value">{activeDetailsPurchase.productName}</span>
                </div>
                <div className="modal-detail-item">
                  <span className="modal-detail-label">Topmate Order ID</span>
                  <span className="modal-detail-value" style={{ fontFamily: "monospace" }}>
                    {activeDetailsPurchase.topmateOrderId}
                  </span>
                </div>
                <div className="modal-detail-item">
                  <span className="modal-detail-label">Purchase Time (IST)</span>
                  <span className="modal-detail-value" style={{ color: "#38bdf8" }}>
                    {activeDetailsPurchase.purchaseDateIST}
                  </span>
                </div>
                <div className="modal-detail-item">
                  <span className="modal-detail-label">Amount Paid</span>
                  <span className="modal-detail-value" style={{ color: "#10b981" }}>
                    {activeDetailsPurchase.currency} {activeDetailsPurchase.amount}
                  </span>
                </div>
                <div className="modal-detail-item">
                  <span className="modal-detail-label">Payment Status</span>
                  <span className="modal-detail-value">
                    <span className="status-pill paid">{activeDetailsPurchase.paymentStatus}</span>
                  </span>
                </div>
                <div className="modal-detail-item">
                  <span className="modal-detail-label">Automatic Email Status</span>
                  <span className="modal-detail-value">
                    {activeDetailsPurchase.emailSent ? (
                      <span style={{ color: "#34d399" }}>
                        Confirmation email sent automatically ({activeDetailsPurchase.emailSentCount || 1} time(s))
                      </span>
                    ) : (
                      <span style={{ color: "#fbbf24" }}>Pending automatic dispatch</span>
                    )}
                  </span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="topmate-btn-secondary"
                onClick={() => setActiveDetailsPurchase(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TopmatePurchases;
